import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  NewsContainer,
  NewsCard,
  CardHeader,
  HeaderContent,
  NewsTitle,
  ShowMoreButton,
  Divider,
  NewsContent,
  NewsItem,
  NewsTextSection,
  NewsSource,
  NewsHeadline,
  NewsTimestamp,
  NewsImage,
  LoadingSpinner,
  ErrorMessage,
  NewsScrollContainer,
  RetryButton,
} from "./News.styles";
import { useNewsData } from "features/Market/hook";

const News = ({ strategyId }) => {
  const [showAll, setShowAll] = useState(false);
  const scrollRef = useRef(null);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInHours < 1) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes} minutes ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
      } else if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
      } else {
        // For dates older than a week, show the actual date
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year:
            date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
        });
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Fallback to original string if parsing fails
    }
  };

  const {
    news,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useNewsData(strategyId);

  const handleShowMore = () => {
    setShowAll(true);
  };

  // Display only first 5 items initially, then all when showAll is true
  const displayedNews = showAll ? news : news?.slice(0, 5);
  const shouldShowButton = !showAll && news && news.length > 5;

  // Infinite scroll: when near bottom of the scroll container, fetch next page
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50; // threshold
      if (nearBottom && showAll && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [showAll, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isError || !displayedNews) {
    return null;
  }

  const redirectToArticle = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <NewsContainer>
      <NewsCard>
        <CardHeader>
          <HeaderContent>
            <NewsTitle>News</NewsTitle>
            {shouldShowButton && (
              <ShowMoreButton
                onClick={handleShowMore}
                aria-label="Show more news"
                tabIndex={0}
              >
                Show More
              </ShowMoreButton>
            )}
          </HeaderContent>
          <Divider />
        </CardHeader>

        {isLoading ? (
          <LoadingSpinner>Loading news...</LoadingSpinner>
        ) : (
          <NewsScrollContainer ref={scrollRef} maxHeight={showAll}>
            <NewsContent>
              {displayedNews?.map((newsItem, index) => {
                return (
                  <NewsItem
                    key={`${newsItem.url}-${index}`}
                    onClick={() => redirectToArticle(newsItem.url)}
                  >
                    <NewsTextSection>
                      <NewsSource>{newsItem.site}</NewsSource>
                      <NewsHeadline>{newsItem.title}</NewsHeadline>
                      <NewsTimestamp>
                        {formatDate(newsItem.publishedDate)}
                      </NewsTimestamp>
                    </NewsTextSection>
                    {newsItem.image && (
                      <NewsImage
                        src={newsItem.image}
                        alt={`News image for ${newsItem.title}`}
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                  </NewsItem>
                );
              })}
              {(!displayedNews || displayedNews.length === 0) && (
                <LoadingSpinner>No news available</LoadingSpinner>
              )}
              {/* Loading indicator when fetching next page */}
              {showAll && isFetchingNextPage && (
                <LoadingSpinner>Loading more news...</LoadingSpinner>
              )}
              {/* No more items indicator */}
              {showAll &&
                !hasNextPage &&
                !isFetchingNextPage &&
                news?.length > 0 && (
                  <LoadingSpinner>You&apos;re caught up!</LoadingSpinner>
                )}
              {/* Next page error handling */}
              {showAll && !isLoading && error && (
                <ErrorMessage>
                  Failed to load more news.
                  <RetryButton onClick={() => fetchNextPage()}>
                    Retry
                  </RetryButton>
                </ErrorMessage>
              )}
            </NewsContent>
          </NewsScrollContainer>
        )}
      </NewsCard>
    </NewsContainer>
  );
};

News.propTypes = {
  strategyId: PropTypes.string,
};

export default News;
